/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import * as _ from 'lodash';
import * as moment from 'moment';

import {DataViewResource} from '../../../components/api/resources/resource/data-view';
import template from './line-item-list-widget.component.html';
import {OBJECT_TYPE} from '../../common/constants/object-type';
import {ALL_FIELDS, WIDGET_TYPE} from '../../common/constants/widget-type';
import {
  WorkspaceListColumn,
  isWorkspaceColumnSortable,
  workspaceChangePage,
  workspaceColumnLabel,
  workspacePageNumbers,
  workspacePageRangeEnd,
  workspacePageRangeStart,
  workspaceSortBy,
  workspaceTotalPages
} from './workspace-widget-list-commons';
import {networkDayFromApiDate} from '../../order/order-budget-utils';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {StarService} from '../../../components/star/star.service';

@Component({
  standalone: false,
  selector: 'adn-line-item-list-widget',
  template
})
export class LineItemListWidgetComponent implements OnInit {
  @Input() widgetType: any;

  list: any = {};
  /** Snapshot — list-helper replaces list.items on settle (NG0100). */
  displayItems: any[] = [];
  hasItems = false;
  listPageSize = 25;
  listTotalCount = 0;
  listCurrentPage = 1;
  displayPage: any = {pageSize: 25, totalCount: 0, currentPage: 1};
  fields: any[] = [];
  columnHeaderFields: WorkspaceListColumn[] = [];

  readonly isColumnSortable = isWorkspaceColumnSortable;
  readonly columnLabel = workspaceColumnLabel;
  readonly sortBy = workspaceSortBy;
  readonly changePage = workspaceChangePage;
  readonly totalPages = workspaceTotalPages;
  readonly pageNumbers = workspacePageNumbers;
  readonly pageRangeStart = workspacePageRangeStart;
  readonly pageRangeEnd = workspacePageRangeEnd;

  constructor(
    private readonly adnListHelper: AdnListHelperService,
    @Inject(StarService) private readonly StarService: any,
    private readonly dataViewResource: DataViewResource,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    const sysDefined = _.cloneDeep(WIDGET_TYPE[this.widgetType.systemDefined.id].systemDefined);
    const widgetParams = sysDefined.widgetParams;
    const useStarredIds = sysDefined.starredIds;

    this.fields = _.compact(_.map(this.widgetType.fields, (f) => ALL_FIELDS[f]));

    const sortableFields: Record<string, boolean> = {
      name: true,
      startDate: true,
      endDate: true,
      velocity: true,
      deliveryPercentage: true,
      elapsedTimePercentage: true
    };

    this.columnHeaderFields = _.map(this.fields, (f: any) => {
      if (sortableFields[f.id]) {
        return {param: f.id, numeric: f.numeric};
      }
      const fieldInformation: WorkspaceListColumn = {param: '', label: f.id, numeric: f.numeric};
      const trans = this.LocalNetworkProfile.getNetworkTranslations(f.transKey || f.id);
      if (trans) {
        fieldInformation.labelInstant = trans;
      }
      return fieldInformation;
    });

    let ids: string[];
    if (useStarredIds) {
      ids = this.StarService.getStarred(OBJECT_TYPE.LineItem);
      if (ids.length === 0) {
        this.list = {items: [], query: {$resolved: true}};
        this.displayItems = [];
        this.hasItems = false;
        this.listPageSize = 25;
        this.listTotalCount = 0;
        this.listCurrentPage = 1;
        this.displayPage = {pageSize: 25, totalCount: 0, currentPage: 1};
        return;
      }
    }

    const dataViewParams: any = {
      ids,
      currency: this.LocalNetworkProfile.get('defaultCurrency') || 'EUR'
    };
    if (!Array.isArray(ids)) {
      delete dataViewParams.ids;
    }

    const excludeIfMissing: string[] = [];
    _.forEach(sysDefined.excludeDefaults, (value: any, key: string) => {
      this.widgetType.excludeValues = this.widgetType.excludeValues || {};
      if (Object.prototype.hasOwnProperty.call(this.widgetType.excludeValues, key)) {
        if (this.widgetType.excludeValues[key]) {
          excludeIfMissing.push(key);
        }
      } else if (value.value) {
        excludeIfMissing.push(key);
      }
    });

    const labels = _.get(this.widgetType, 'labels', []);
    let whereString = labels.length > 0 ? labels[0] + ' in lineItem.labels' : '';
    const startDate = this.widgetType.startDate
      ? networkDayFromApiDate(this.widgetType.startDate, this.adnTimeShifter)
      : null;
    const endDate = this.widgetType.endDate
      ? networkDayFromApiDate(this.widgetType.endDate, this.adnTimeShifter)
      : null;
    if (sysDefined.hasStartDate && moment(startDate).isValid() && moment(endDate).isValid()) {
      whereString += whereString.length > 0 ? ';' : '';
      whereString += 'lineItem.startDate>=' + startDate + ';lineItem.endDate<=' + endDate;
    }

    this.adnListHelper.setUpBasicList(this.list, this.dataViewResource, this.widgetType.id, {
      listMethod: 'getView',
      moreParams: dataViewParams,
      viewModel: 'LineItemListView',
      where: whereString,
      filterBy: _.get(widgetParams, 'filterBy') || '',
      filterByValue: _.get(widgetParams, 'filterByValue') || '',
      excludeIfMissing,
      pageSize: Math.min((_.isFinite(this.widgetType.rows) && this.widgetType.rows > 0 ? this.widgetType.rows : 10), 100),
      afterFill: () => {
        deferMarkForCheck(this.cdr, () => {
          _.forEach(this.list.items, (row: any) => {
            row.velocity = _.isFinite(row.velocity) ? row.velocity : null;
          });
          this.displayItems = (this.list.items || []).slice();
          this.hasItems = this.displayItems.length > 0;
          this.listPageSize = this.list.pageSize || 25;
          this.listTotalCount = this.list.totalCount || 0;
          this.listCurrentPage = this.list.currentPage || 1;
          this.displayPage = {
            pageSize: this.listPageSize,
            totalCount: this.listTotalCount,
            currentPage: this.listCurrentPage
          };
        });
      }
    });
  }

  fieldType(field: any): string {
    return field?.type;
  }

  hasNumberValue(row: any, field: any): boolean {
    return row[field.id] >= 0;
  }

  fieldTranslate(key: string): string {
    return this.translate.instant(key);
  }
}
